import React from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon } from '@/shared/assets/icons/chevron-down-icon';
import { ChevronUpIcon } from '@/shared/assets/icons/chevron-up-icon';
import { DarkThemeIcon } from '@/shared/assets/icons/dark-theme-icon';
import { LightThemeIcon } from '@/shared/assets/icons/light-theme-icon';
import { MidnightBlueThemeIcon } from '@/shared/assets/icons/midnight-blue-theme-icon';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { PopoverController } from '@/shared/components/ui/popover/components/popover-controller/popover-controller';
import {
  ThemeSwitcherMenuItem,
  ThemeSwitcherSelect,
} from '@/shared/features/theme-switcher/components/theme-switcher.styled';
import { TThemeSwitcher } from '@/shared/features/theme-switcher/components/theme-switcher.types';
import { useAppTheme } from '@/shared/features/theme-switcher/context/theme-context';
import { ThemeEnum } from '@/shared/types/common.types';

export const ThemeSwitcher: React.FC<TThemeSwitcher> = ({ component }) => {
  const { selectedTheme, setSelectedTheme } = useAppTheme();
  const { t } = useTranslation();
  const label = t(`ui:theme.${selectedTheme}`);

  const getSelectedThemeIcon = (type: ThemeEnum) => {
    if (type === ThemeEnum.LIGHT) {
      return <LightThemeIcon />;
    }

    if (type === ThemeEnum.MIDNIGHT_BLUE) {
      return <MidnightBlueThemeIcon />;
    }

    return <DarkThemeIcon />;
  };

  const getTriggerComponent = (open: boolean) => {
    if (component === 'MenuItem') {
      return (
        <ThemeSwitcherMenuItem
          icon={getSelectedThemeIcon(selectedTheme)}
          alignLabelLeft
          variant="primary"
        >
          {label}
        </ThemeSwitcherMenuItem>
      );
    }

    return (
      <ThemeSwitcherSelect
        variant="secondary"
        size="large"
        icon={getSelectedThemeIcon(selectedTheme)}
        tooltip={label}
      >
        {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </ThemeSwitcherSelect>
    );
  };

  return (
    <PopoverController
      trigger={(popoverState) => getTriggerComponent(popoverState)}
    >
      {(popoverClose) =>
        Object.values(ThemeEnum).map((themeType) => (
          <MenuItem
            key={themeType}
            value={themeType}
            onClick={() => {
              setSelectedTheme(themeType);
              popoverClose();
            }}
            selected={selectedTheme === themeType}
            icon={getSelectedThemeIcon(themeType)}
          >
            {t(`ui:theme.${themeType}`)}
          </MenuItem>
        ))
      }
    </PopoverController>
  );
};
